import Vue from "vue";
import i18n from "@/services/i18n/i18n";
import { extend, localize } from "vee-validate";
import {
  required,
  email,
  min,
  max,
  alpha,
  alpha_num,
  numeric,
  length,
  alpha_dash,
  alpha_spaces,
  min_value,
  max_value,
  excluded,
  confirmed,
  required_if,
  integer,
  is_not,
  digits,
  regex,
  is,
  size,
  dimensions,
} from "vee-validate/dist/rules";
import ar from "vee-validate/dist/locale/ar.json";
import en from "vee-validate/dist/locale/en.json";
import customEn from "@/services/i18n/locales/en/customValidation.json";
import customAr from "@/services/i18n/locales/ar/customValidation.json";
import moment from "moment";

extend("required", required);
extend("email", email);
extend("min", min);
extend("max", max);
extend("min_value", min_value);
extend("max_value", max_value);
extend("alpha", alpha);
extend("alpha_num", alpha_num);
extend("numeric", numeric);
extend("length", length);
extend("alpha_dash", alpha_dash);
extend("alpha_spaces", alpha_spaces);
extend("excluded", excluded);
extend("confirmed", confirmed);
extend("required_if", required_if);
extend("integer", integer);
extend("is_not", is_not);
extend("digits", digits);
extend("regex", regex);
extend("is", is);
extend("size", size);
extend("dimensions", dimensions);

// Custom rules for
extend("latest_number", {
  validate(value, { numbers }) {
    return +value > +numbers[0];
  },
  params: ["numbers"],
  message: (fieldName, placeholders) => {
    return i18n.t("latest_number", {
      fieldName,
      number: placeholders.numbers[0],
    });
  },
});

extend("oldest_number", {
  validate(value, { numbers }) {
    return +value <= +numbers[1];
  },
  params: ["numbers"],
  message: (fieldName, placeholders) => {
    return i18n.t("oldest_number", {
      fieldName,
      placeholders1: placeholders.numbers[2],
      placeholders2: placeholders.numbers[1] || 0,
    });
  },
});

extend("oldest_date", {
  validate(value, { dates }) {
    const parsedDates = dates?.map((dateString) => new Date(dateString));
    const currentDate = new Date(value);

    return parsedDates.every((date) => currentDate >= date);
  },
  params: ["dates"],
});

extend("start_before_end", {
  validate(_, { startDate, endDate }) {
    if (!startDate || !endDate) {
      return true;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);

    return end > start;
  },
  params: ["startDate", "endDate"],
});

extend("max_hour_difference", {
  validate(_, { from, to, hours }) {
    if (!from || !to || isNaN(hours)) return true;

    const fromDate = new Date(from);
    const toDate = new Date(to);

    const diffInMs = toDate - fromDate;
    const diffInHours = diffInMs / (1000 * 60 * 60);

    return diffInHours <= hours;
  },
  params: ["from", "to", "hours"],
  message: (_, { hours }) => i18n.t("max_hour_difference", { hours }),
});

extend("no_spaces", {
  validate(value) {
    const regex = /^\S+$/;
    return regex.test(value);
  },
});

extend("arabic_text", {
  validate(value) {
    return /^([\u0621-\u064A\u0652\u064B-\u0655\u0660-\u0669\s!٪"#$%&'()*+,-./:;<=>?@[\]^_`{|}~٫0123456789]+)$/.test(
      value
    );
  },
});

extend("arabic_text_with_special_character", {
  validate(value) {
    return /^([\u0621-\u064A\u0652\u064B-\u0655\u0660-\u0669\s!٪"#$%&'()*+,-./:;<=>?@[\]^_`{|}~٫0123456789]+)$/.test(
      value
    );
  },
});

extend("prevent_arabic_text", {
  validate(value) {
    return /^[^\u0621-\u064A\u0660-\u0669]*$/.test(value);
  },
});

extend("positive", (value) => {
  if (value >= 0) {
    return true;
  }
  return i18n.t("positive-validation-msg");
});

extend("decimalDigits", {
  params: ["digits"],
  validate: (value, { digits }) => {
    const regex = new RegExp(`^\\d+(\\.\\d{1,${digits}})?$`);
    return regex.test(value);
  },
  message: (_, { digits }) => i18n.t(digits === 1 ? "decimalOneDigit" : "decimalDigits", { digits }),
});

extend("english_text_only", (value) => {
  return/^[a-zA-Z0-9-_]+$/.test(value);
});

extend("english_text", (value) => {
  return /^([a-zA-Z0-9!٪"#$%&'()*+,-./:;<=>?@[\]^_`{|}~٠-٩]+\s)*[a-zA-Z0-9!٪"#$%&'()*+,-./:;<=>?@[\]^_`{|}~٠-٩]+$/.test(
    value
  );
});

extend("maxValue", {
  validate: (value, { args }) => {
    const maxValue = +args;
    return value <= maxValue;
  },
  params: ["args"],
});

extend("is_not", {
  validate(value, { valueToCompare }) {
    return value !== valueToCompare;
  },
  params: ["valueToCompare"],
});

extend("is_not_future_date", {
  validate: (value) => {
    const date1 = moment(value).startOf("day");
    const date2 = moment().startOf("day");
    const diff = date1.diff(date2, "days");
    return diff < 0;
  },
});

extend("vat_number", (value) => {
  return /^3\d{13}3$/.test(value);
});

extend("from_to_length", {
  params: ['min', 'max'],
  validate(value, { min, max }) {
    const regex = new RegExp(`^\\d{${min},${max}}$`);
    return regex.test(value);
  },
});

extend("not_in_past", {
  validate: (value) => {
    const date = moment(value).startOf("day");
    const today = moment().startOf("day");
    return date >= today;
  },
});

extend("optional_positive_integer", (value) => {
  if (value === null || value === undefined || value === "") {
    return true;
  }
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
    return i18n.t("optional-positive-integer-validation-msg");
  }
  return true;
});

localize({
  en: {
    messages: { ...en.messages, ...customEn },
  },
  ar: {
    messages: { ...ar.messages, ...customAr },
  },
});

let LOCALE = "en";

// A simple get/set interface to manage our locale in components.
// This is not reactive, so don't create any computed properties/watchers off it.
Object.defineProperty(Vue.prototype, "locale", {
  get() {
    return LOCALE;
  },
  set(val) {
    LOCALE = val;
    localize(val);
  },
});
